import {
  buildingAreasOf,
  buildingAreaRows,
  hasAnyBuildingArea,
  type ProjectBuildingAreaSource,
} from "@/features/BuildingProject/services/buildingAreaWriteInput";

/**
 * The Building area section's slice of the project, in the terms a SAVE understands.
 *
 * Its own module, and pure, because `settingsSections.ts` cannot be imported into a test — its
 * section components pull in `@lucide/vue` — and this is the one fingerprint on that page whose
 * correctness is not obvious by reading it.
 *
 * ## Why the numbers are coerced rather than compared raw
 *
 * The three below-ground columns are NOT NULL in Postgres, so there is no representation of "unset"
 * for any of the six: a save writes an unanswered field as 0 (`buildingAreaRows`) and the read path
 * hands that 0 straight back (`projectWrapperFromHasura`). The fingerprint used to be the raw
 * `referencedAreas` and `heatArea` objects, where `null` and `0` are different values — so the
 * project came back from its own save holding numbers that did not match the snapshot taken a moment
 * earlier, and the rail reported "1 section edited, not saved yet" for a save the reader had just
 * made (RTLCA-5782). Only a second, byte-identical save cleared it.
 *
 * Comparing the ROWS instead states the invariant the rail is supposed to have: it lights exactly
 * when a save would store something different. `null` and `0` are the same stored value for these
 * six, so they compare equal — for these six fields only, and only because the database cannot tell
 * them apart either.
 *
 * ## Why `stored` is here too
 *
 * A project nobody has answered gets no rows at all (`hasAnyBuildingArea`), which is how the fields
 * stay blank instead of reading back as a 0 m² building (RTLCA-5784). Typing a 0 into an empty
 * project therefore IS a change — it creates the rows — while coercing to the same six zeros. The
 * flag is what the marker sees in that case; without it the reader's first 0 would leave the Save
 * bar inactive with nothing they could do about it.
 *
 * ## What is deliberately left out
 *
 * `totalArea` and `totalReferencedAreaInclBasement`, both derived: `project_area_calculate` sums the
 * rows for the first and nothing writes the second. A field left out cannot light the marker, which
 * is the safe direction, and both move only when a number captured above has already moved.
 */
export const buildingAreaFingerprint = (project: ProjectBuildingAreaSource | null | undefined) => {
  const areas = buildingAreasOf(project);
  const referencedAreas = project?.emissionCalculationAssumptions?.referencedAreas;

  return {
    // Whether the two rows exist at all, which is a change the coerced numbers below cannot show.
    stored: hasAnyBuildingArea(areas),
    // `projectId` dropped: it is the same project on both sides of the comparison, so it can only
    // ever add noise. A mixed-use project writes a pair per use, hence the type.
    written: buildingAreaRows(areas).map(({ buildingTypeId, buildingAreaType, area, floors, heatedArea }) => ({
      buildingTypeId,
      buildingAreaType,
      area,
      floors,
      heatedArea,
    })),
    // The reference areas, verbatim. Their own table is nullable, so `null` and `0` really are
    // different answers there and there is nothing to coerce.
    referenceAreaValues: referencedAreas?.values,
    referenceAreaEntries: referencedAreas?.entries,
  };
};

export default buildingAreaFingerprint;
